// Deep intake (Flow 2): the client side of POST /intake and the two plan calls the intake/home screens need.
// The schema lives on the server (supabase/functions/_shared/intake.ts); the slices mirrored here (merge, coverage,
// place coverage) exist only so a tap shows up instantly, before the server answers. The server stays the truth.
import { callFn } from './api'
import { slugLabel } from './format'
import { f2 } from '../copyFlow2'

export type Intake = Record<string, Record<string, unknown>>
export type Group =
  | 'who' | 'when' | 'where' | 'favorites' | 'interests' | 'physical' | 'social' | 'occasion' | 'budget' | 'pace'
  | 'novelty' | 'mode' | 'setting' | 'rhythm' | 'structure' | 'food' | 'practical' | 'never' | 'about'

/** Server order (SPEC). `cues` from the server follow it; the rack falls back to it. */
export const GROUPS: Group[] = ['who', 'when', 'where', 'favorites', 'interests', 'physical', 'social', 'occasion', 'budget', 'pace', 'novelty', 'mode', 'setting', 'rhythm', 'structure', 'food', 'practical', 'never', 'about']
export const BASICS: Group[] = ['who', 'when', 'where']
/** Every topic has a cue card ('about' last: optional, and only what they choose to say). */
export const CUE_GROUPS: Group[] = GROUPS
/** Astra's copy keys say food_drink where the schema says food. */
export const copyKey = (g: Group) => (g === 'food' ? 'food_drink' : g)
export const topicLabel = (g: Group) => f2(`topic.${copyKey(g)}`)

export type IntakeResponse = {
  intake: Intake
  heard: Intake
  coverage: Record<string, boolean>
  readiness: number
  cues: string[]
  can_go: boolean
  place: { covered: boolean; label: string }
}
export type IntakeBody = { say?: string; cue?: Group; set?: Intake | Record<string, Record<string, unknown> | null>; done?: true; reset?: true }

export const postIntake = (body: IntakeBody) => callFn<IntakeResponse>('intake', body as Record<string, unknown>)

// ---------- plans (the slice home + intake need; the menu/itinerary data layer is lib/plan.ts) ----------
export type PlanRow = {
  id: string
  title: string | null
  party: { kind?: string; size?: number; with?: string } | null
  dest: { label?: string; scope?: string; covered?: boolean; areas?: string[]; city?: string; tz?: string; building?: boolean; need_city?: boolean } | null
  start_date: string
  end_date: string
  items: number
  past: boolean
  mine: boolean
  status?: string
  updated_at?: string
}
export type CreatePlan = { from?: string; to?: string; title?: string; scope?: string; areas?: string[]; place?: string }
export const listPlans = () => callFn<{ plans: PlanRow[] }>('plan', { list: true })
export const createPlan = (create: CreatePlan = {}) => callFn<{ plan: PlanRow & { dest: PlanRow['dest'] } }>('plan', { create })
export const addToPlan = (plan: string, item: string) => callFn<{ plan_item: { id: string } }>('plan', { add: { plan, item } })

/** The plan the person is working in, so Menu / Itinerary in the navigation know where to go. */
const PLAN_KEY = 'mg_plan'
export function currentPlan(): string | null {
  try {
    return localStorage.getItem(PLAN_KEY)
  } catch {
    return null
  }
}
export function rememberPlan(id: string) {
  try {
    localStorage.setItem(PLAN_KEY, id)
  } catch {
    /* navigation convenience only */
  }
}

// ---------- local mirror of the server's merge + coverage (instant taps) ----------
const UNION = new Set(['meta.no_pref', 'favorites.loved', 'favorites.been', 'interests.passions', 'never.tags', 'never.words', 'food.diet', 'about.languages'])
export const isUnion = (g: string, f: string) => UNION.has(`${g}.${f}`)

/** Same semantics as the server's merge (null removes, union lists, weights spread), without validation. */
export function mergeLocal(cur: Intake, patch: IntakeBody['set']): Intake {
  const out: Intake = JSON.parse(JSON.stringify(cur ?? {}))
  if (!patch) return out
  for (const [g, fields] of Object.entries(patch)) {
    if (fields === null) {
      delete out[g]
      continue
    }
    out[g] = out[g] ?? {}
    for (const [k, v] of Object.entries(fields)) {
      if (v === null || v === undefined) delete out[g][k]
      else if (isUnion(g, k) && Array.isArray(out[g][k]) && Array.isArray(v)) out[g][k] = [...new Set([...(out[g][k] as unknown[]), ...v])]
      else if (k === 'weights' && out[g][k] && typeof v === 'object') out[g][k] = { ...(out[g][k] as object), ...(v as object) }
      else out[g][k] = v
    }
    if (!Object.keys(out[g]).length) delete out[g]
  }
  return out
}

/** Topics answered with an explicit "No preference" (server: meta.no_pref; counts as answered). */
export const noPrefOf = (i: Intake) => ((i.meta?.no_pref ?? []) as string[])
export const noPrefPatch = (g: Group): IntakeBody['set'] => ({ meta: { no_pref: [g] } })

export function coverageOf(i: Intake) {
  const noPref = noPrefOf(i)
  const has = (g: string) => Object.keys(i[g] ?? {}).length > 0 || noPref.includes(g)
  const cov: Record<string, boolean> = Object.fromEntries(GROUPS.map((g) => [g, has(g)]))
  cov.when = Boolean(i.when && (i.when.from || i.when.days || i.when.parts))
  cov.where = Boolean(i.where && (i.where.base || i.where.areas || i.where.place || i.where.scope))
  cov.who = Boolean(i.who?.kind)
  const rest = GROUPS.filter((g) => !BASICS.includes(g))
  const readiness = Math.round(BASICS.filter((g) => cov[g]).length * 15 + (rest.filter((g) => cov[g]).length / rest.length) * 55)
  return { coverage: cov, readiness, canGo: BASICS.every((g) => cov[g]) }
}

/** Mirrors the server's placeCovered: anything outside LA is a saved request, not a menu. */
export function placeOf(i: Intake): { covered: boolean; label: string } {
  const raw = String(i.where?.place ?? '')
  const place = raw.toLowerCase()
  const scope = String(i.where?.scope ?? '')
  const la = !place || /\b(la|l\.a\.|los angeles|socal|southern california|long beach|pasadena|santa monica|hollywood|venice|malibu)\b/.test(place)
  if ((scope === 'other_city' || scope === 'trip') && !la) return { covered: false, label: raw || 'there' }
  return { covered: la, label: la ? 'Los Angeles' : raw }
}

// ---------- dates (Los Angeles, where the pool is) ----------
const TZ = 'America/Los_Angeles'
export const todayYmd = () => new Date().toLocaleDateString('en-CA', { timeZone: TZ })
export function addDays(ymd: string, n: number): string {
  const d = new Date(`${ymd}T12:00:00Z`)
  d.setUTCDate(d.getUTCDate() + n)
  return d.toISOString().slice(0, 10)
}
const weekday = (ymd: string) => new Date(`${ymd}T12:00:00Z`).getUTCDay()
/** "This weekend": the coming Saturday and Sunday; on a Sunday, just today. */
export function weekendRange(): { from: string; to: string } {
  const t = todayYmd()
  const wd = weekday(t)
  if (wd === 0) return { from: t, to: t }
  const sat = addDays(t, (6 - wd + 7) % 7)
  return { from: sat, to: addDays(sat, 1) }
}
const dayFmt = new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric' })
const monthFmt = new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', month: 'long', year: 'numeric' })
const fmtDay = (ymd: string) => dayFmt.format(new Date(`${ymd}T12:00:00Z`))
/** "Sat, Oct 3" or "Sat, Oct 3 – Sun, Oct 4"; a whole calendar month reads "March 2027". */
export function formatRange(from: string, to?: string | null): string {
  if (!to || to === from) return fmtDay(from)
  const [y, m] = from.split('-').map(Number)
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate()
  if (from.endsWith('-01') && to === `${from.slice(0, 8)}${String(last).padStart(2, '0')}`) return monthFmt.format(new Date(`${from}T12:00:00Z`))
  return `${fmtDay(from)} – ${fmtDay(to)}`
}
export function monthRange(year: number, month: number): { from: string; to: string } {
  const last = new Date(Date.UTC(year, month + 1, 0)).getUTCDate()
  const mm = String(month + 1).padStart(2, '0')
  return { from: `${year}-${mm}-01`, to: `${year}-${mm}-${String(last).padStart(2, '0')}` }
}

// ---------- places ----------
/** LA areas the ranker knows (names must match the server's AREAS). */
export const LA_AREAS = [
  'Atwater Village', 'Burbank / Glendale', 'Culver City', 'Downtown / Arts District', 'East LA / Boyle Heights', 'Highland Park / Eagle Rock', 'Hollywood',
  'Inglewood / Westchester', 'Koreatown', 'Long Beach', 'Los Feliz', 'Mid-City / Miracle Mile', 'Mount Washington / Glassell Park',
  'North Hollywood / Valley Village', 'Pasadena', 'San Gabriel Valley', 'Santa Monica', 'Silver Lake / Echo Park', 'South Bay',
  'South LA / Leimert Park', 'Studio City / Sherman Oaks', 'Venice / Mar Vista', 'West Hollywood', 'West LA / Westwood',
]
export const AREA_CENTERS: Record<string, [number, number]> = {
  'Atwater Village': [34.118, -118.262],
  'Burbank / Glendale': [34.165, -118.28], 'Culver City': [34.021, -118.396], 'Downtown / Arts District': [34.043, -118.243], 'East LA / Boyle Heights': [34.033, -118.19],
  'Highland Park / Eagle Rock': [34.116, -118.193], 'Hollywood': [34.098, -118.326], 'Inglewood / Westchester': [33.962, -118.353], 'Koreatown': [34.061, -118.3],
  'Long Beach': [33.77, -118.19], 'Los Feliz': [34.106, -118.284], 'Mid-City / Miracle Mile': [34.064, -118.35], 'Mount Washington / Glassell Park': [34.11, -118.225],
  'North Hollywood / Valley Village': [34.172, -118.379], 'Pasadena': [34.148, -118.144], 'San Gabriel Valley': [34.096, -118.106], 'Santa Monica': [34.019, -118.491],
  'Silver Lake / Echo Park': [34.087, -118.27], 'South Bay': [33.86, -118.39], 'South LA / Leimert Park': [34.004, -118.33], 'Studio City / Sherman Oaks': [34.15, -118.42],
  'Venice / Mar Vista': [33.99, -118.46], 'West Hollywood': [34.09, -118.361], 'West LA / Westwood': [34.05, -118.44],
}
/** The LA area nearest a point (Use my location resolves to an area, never a kept address). */
export function nearestArea(lat: number, lng: number): string {
  let best = LA_AREAS[0]
  let d = Infinity
  for (const [name, [a, b]] of Object.entries(AREA_CENTERS)) {
    const dd = (a - lat) ** 2 + ((b - lng) * Math.cos((lat * Math.PI) / 180)) ** 2
    if (dd < d) {
      d = dd
      best = name
    }
  }
  return best
}

/**
 * LA neighborhoods that are not one of the listed areas, with rough centers: typing one offers the nearest listed area
 * (what the ranker knows) while keeping their word as the label (R6: "Frogtown · near Silver Lake / Echo Park").
 */
const LA_HOODS: [string, number, number][] = [
  ['Frogtown', 34.097, -118.245], ['Elysian Valley', 34.097, -118.245], ['Elysian Park', 34.081, -118.238], ['Cypress Park', 34.091, -118.226],
  ['Lincoln Heights', 34.073, -118.209], ['Chinatown', 34.063, -118.238], ['Little Tokyo', 34.05, -118.24], ['Westlake', 34.058, -118.278],
  ['MacArthur Park', 34.058, -118.278], ['East Hollywood', 34.093, -118.296], ['Thai Town', 34.101, -118.305], ['Larchmont', 34.074, -118.323],
  ['Hancock Park', 34.069, -118.333], ['Fairfax', 34.077, -118.361], ['Beverly Hills', 34.073, -118.4], ['Beverly Grove', 34.074, -118.378],
  ['Brentwood', 34.052, -118.473], ['Palms', 34.023, -118.405], ['Del Rey', 33.993, -118.422], ['Playa Vista', 33.976, -118.418],
  ['Playa del Rey', 33.952, -118.442], ['Marina del Rey', 33.98, -118.451], ['El Segundo', 33.919, -118.416], ['Manhattan Beach', 33.885, -118.41],
  ['Hermosa Beach', 33.862, -118.4], ['Redondo Beach', 33.849, -118.388], ['Torrance', 33.836, -118.34], ['San Pedro', 33.736, -118.292],
  ['Compton', 33.896, -118.22], ['Watts', 33.94, -118.242], ['Crenshaw', 34.01, -118.335], ['Baldwin Hills', 34.01, -118.36],
  ['Jefferson Park', 34.027, -118.32], ['West Adams', 34.033, -118.32], ['Exposition Park', 34.015, -118.287], ['University Park', 34.025, -118.285],
  ['Montecito Heights', 34.088, -118.199], ['El Sereno', 34.081, -118.177], ['Alhambra', 34.095, -118.127], ['Monterey Park', 34.062, -118.122],
  ['Arcadia', 34.14, -118.035], ['South Pasadena', 34.116, -118.15], ['Altadena', 34.19, -118.131], ['La Cañada Flintridge', 34.206, -118.2],
  ['Toluca Lake', 34.152, -118.352], ['Universal City', 34.138, -118.353], ['Van Nuys', 34.186, -118.449], ['Encino', 34.159, -118.501],
  ['Tarzana', 34.17, -118.553], ['Woodland Hills', 34.168, -118.605], ['Reseda', 34.201, -118.536], ['Northridge', 34.228, -118.536],
  ['Pacific Palisades', 34.048, -118.526], ['Malibu', 34.036, -118.69], ['Topanga', 34.094, -118.6], ['Sawtelle', 34.035, -118.445],
  ['Century City', 34.056, -118.416], ['Bel Air', 34.083, -118.448], ['Hollywood Hills', 34.12, -118.33], ['Laurel Canyon', 34.12, -118.37],
  ['Griffith Park', 34.137, -118.294], ['Echo Park', 34.078, -118.26], ['Silver Lake', 34.087, -118.27], ['Eagle Rock', 34.139, -118.21],
  ['Glendale', 34.142, -118.255], ['Burbank', 34.181, -118.309], ['Sherman Oaks', 34.151, -118.449], ['Studio City', 34.14, -118.391],
  ['Mar Vista', 34.002, -118.43], ['Venice', 33.99, -118.46], ['Westwood', 34.063, -118.445], ['Boyle Heights', 34.033, -118.205],
  ['Arts District', 34.04, -118.233], ['Downtown', 34.047, -118.25], ['DTLA', 34.047, -118.25], ['Leimert Park', 34.004, -118.33],
  ['Inglewood', 33.962, -118.353], ['Westchester', 33.96, -118.4], ['Miracle Mile', 34.062, -118.35], ['Mid-City', 34.04, -118.35],
  ['Glassell Park', 34.117, -118.233], ['Mount Washington', 34.105, -118.217], ['North Hollywood', 34.172, -118.379], ['Valley Village', 34.165, -118.396],
]

/**
 * A small gazetteer so "Tokyo" resolves to "Tokyo, Japan" with its type. No coordinates are invented for these: a
 * place outside LA is only ever saved as a request. Anything not listed can still be entered by hand.
 */
export type PlaceResult = { kind: 'area' | 'la' | 'city' | 'near'; name: string; region: string; type: string; abroad?: boolean; area?: string }
const CITIES: [string, string, boolean][] = [
  ['New York', 'New York, USA', false], ['San Francisco', 'California, USA', false], ['San Diego', 'California, USA', false], ['Palm Springs', 'California, USA', false],
  ['Santa Barbara', 'California, USA', false], ['Las Vegas', 'Nevada, USA', false], ['Chicago', 'Illinois, USA', false], ['Austin', 'Texas, USA', false],
  ['Seattle', 'Washington, USA', false], ['Portland', 'Oregon, USA', false], ['Miami', 'Florida, USA', false], ['New Orleans', 'Louisiana, USA', false],
  ['Nashville', 'Tennessee, USA', false], ['Denver', 'Colorado, USA', false], ['Boston', 'Massachusetts, USA', false], ['Honolulu', 'Hawaii, USA', false],
  ['Tokyo', 'Japan', true], ['Kyoto', 'Japan', true], ['Osaka', 'Japan', true], ['Seoul', 'South Korea', true], ['Taipei', 'Taiwan', true],
  ['Hong Kong', 'China', true], ['Bangkok', 'Thailand', true], ['Singapore', 'Singapore', true], ['Bali', 'Indonesia', true], ['Sydney', 'Australia', true],
  ['Auckland', 'New Zealand', true], ['London', 'United Kingdom', true], ['Paris', 'France', true], ['Rome', 'Italy', true], ['Florence', 'Italy', true],
  ['Barcelona', 'Spain', true], ['Madrid', 'Spain', true], ['Lisbon', 'Portugal', true], ['Amsterdam', 'Netherlands', true], ['Berlin', 'Germany', true],
  ['Copenhagen', 'Denmark', true], ['Istanbul', 'Türkiye', true], ['Mexico City', 'Mexico', true], ['Oaxaca', 'Mexico', true], ['Vancouver', 'Canada', true],
  ['Montreal', 'Canada', true], ['Buenos Aires', 'Argentina', true], ['Cape Town', 'South Africa', true], ['Marrakech', 'Morocco', true],
]
export function searchPlaces(q: string): PlaceResult[] {
  const s = q.trim().toLowerCase()
  if (!s) return []
  const out: PlaceResult[] = []
  if ('los angeles'.includes(s) || s === 'la' || s === 'l.a.') out.push({ kind: 'la', name: 'Los Angeles', region: 'California', type: 'City' })
  for (const a of LA_AREAS) if (a.toLowerCase().includes(s)) out.push({ kind: 'area', name: a, region: 'Los Angeles', type: 'Neighborhood' })
  // a neighborhood we know but don't list: their word, near the listed area the ranker measures from
  if (s.length >= 3) {
    for (const [name, lat, lng] of LA_HOODS) {
      if (!name.toLowerCase().includes(s) || out.some((r) => r.name === name)) continue
      if (LA_AREAS.some((a) => a.toLowerCase().split(' / ').includes(name.toLowerCase()))) continue
      const area = nearestArea(lat, lng)
      if (area.toLowerCase() === name.toLowerCase() || out.some((r) => r.kind === 'near' && r.name === name)) continue
      out.push({ kind: 'near', name, region: 'Los Angeles', type: 'Neighborhood', area })
    }
  }
  for (const [name, region, abroad] of CITIES) {
    if (name.toLowerCase().includes(s) || region.toLowerCase().includes(s)) out.push({ kind: 'city', name, region, type: 'City', abroad })
  }
  return out.slice(0, 8)
}

// ---------- We heard: one chip per accepted preference ----------
/** `short`: how the chip reads in a playback line ("Got it: Atwater Village, Sat, Oct 3, from 11 AM"). */
export type Chip = { id: string; group: Group; field: string; label: string; value?: unknown; short?: string }

const lean = (v: unknown, neg: string, pos: string) => (typeof v !== 'number' ? null : Math.abs(v) < 0.15 ? f2('intake.no_preference') : f2(v < 0 ? neg : pos))
const yesNo = (title: string, v: unknown) => (typeof v === 'boolean' ? `${title}: ${f2(v ? 'tap.preference.yes' : 'tap.preference.no')}` : null)
const tagLabel = (t: string) => slugLabel(t.startsWith('cat:') ? t.slice(4) : t, true)
const TIER: Record<string, string> = { free: 'tap.budget.free', cheap: 'tap.budget.low', mid: 'tap.budget.mid', splurge: 'tap.budget.high' }
const LEVEL = ['tap.active.easy', 'tap.active.light', 'tap.active.active', 'tap.active.push']
const SCOPE: Record<string, string> = { walkable: 'tap.where.walkable', near: 'tap.where.near', areas: 'tap.where.areas', city: 'tap.where.city', daytrip: 'tap.where.day_trips', other_city: 'tap.where.another_city', trip: 'tap.where.abroad' }
const TRANSPORT: Record<string, string> = { car: 'tap.transport.car', transit: 'tap.transport.transit', rideshare: 'tap.transport.rideshare', walk: 'tap.transport.walk', bike: 'tap.transport.bike' }
const WITH: Record<string, string> = { partner: 'tap.who.partner', date: 'tap.who.partner', friends: 'tap.who.friends', family: 'tap.who.family', kids: 'tap.who.children', coworkers: 'tap.who.coworkers', visitors: 'tap.who.visitors' }
const PART: Record<string, string> = { morning: 'tap.when.morning', afternoon: 'tap.when.afternoon', evening: 'tap.when.evening', late: 'tap.when.late' }
export const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const hour = (h: number) => new Intl.DateTimeFormat('en-US', { hour: 'numeric', timeZone: 'UTC' }).format(new Date(Date.UTC(2026, 0, 1, h % 24)))

/** Party in a few words: "Just me", "Two of us", "A group of 5". */
export function partyLabel(who: Record<string, unknown> | undefined): string | null {
  const k = who?.kind
  if (k === 'solo') return f2('tap.who.solo')
  if (k === 'two') return f2('tap.who.two')
  const g = who?.with === 'family' ? f2('tap.who.family') : f2('tap.who.group')
  if (k === 'group') return typeof who?.size === 'number' ? `${g} · ${who.size}` : g
  return null
}

const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()
const andList = (xs: (string | number)[]) => (xs.length <= 1 ? xs.join('') : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`)
const OCCASIONS = new Set(['date', 'solo', 'friends', 'new', 'meet', 'celebrate', 'visitors', 'family'])

/**
 * We heard, one chip per meaning. `server`: the intake as the server last returned it; a budget shows only when it
 * is there (R7: a budget nobody said appeared), never from a local guess.
 */
export function chipsOf(i: Intake, server?: Intake): Chip[] {
  const out: Chip[] = []
  const seen = new Set<string>()
  const push = (group: Group, field: string, label: string | null, value?: unknown, short?: string) => {
    // one chip per meaning, across topics: "live music" said in words and the Live music tag are one preference,
    // and "Outdoors" under Setting and under Interests is said once (R6)
    const key = !label ? '' : label === f2('intake.no_preference') ? `${group}:no-pref` : norm(label)
    if (!label || !key || seen.has(key)) return
    seen.add(key)
    out.push({ id: `${group}.${field}${value !== undefined ? `.${String(value)}` : ''}`, group, field, label, value, ...(short ? { short } : {}) })
  }
  const each = (group: Group, field: string, fmt: (v: string) => string = (v) => v) => {
    const arr = i[group]?.[field]
    if (Array.isArray(arr)) for (const v of arr) push(group, field, fmt(String(v)), String(v))
  }
  const w = i.who ?? {}
  push('who', 'kind', partyLabel(w))
  // "Family · 4" already says family
  if (typeof w.with === 'string' && !(w.with === 'family' && w.kind === 'group')) push('who', 'with', WITH[w.with] ? f2(WITH[w.with]) : slugLabel(w.with, true))
  if (Array.isArray(w.kids_ages) && w.kids_ages.length) push('who', 'kids_ages', f2('heard.kids', { ages: andList(w.kids_ages as number[]) }))
  if (w.older_adults === true) push('who', 'older_adults', f2('tap.who.older'))

  const wh = i.when ?? {}
  if (typeof wh.from === 'string') push('when', 'from', formatRange(wh.from, typeof wh.to === 'string' ? wh.to : null))
  // weekdays add nothing beside a short date range ("Sat, Oct 3" + "Sat"); they matter for a long window
  const span = typeof wh.from === 'string' && typeof wh.to === 'string' ? (new Date(wh.to).getTime() - new Date(wh.from).getTime()) / 86400e3 : null
  if (Array.isArray(wh.days) && (span === null || span >= 7)) push('when', 'days', (wh.days as number[]).map((d) => DAY_NAMES[d]).join(', '))
  each('when', 'parts', (p) => (PART[p] ? f2(PART[p]) : p))
  if (typeof wh.earliest_hour === 'number') push('when', 'earliest_hour', `${f2('tap.when.earliest')} ${hour(wh.earliest_hour)}`, undefined, f2('heard.from', { hour: hour(wh.earliest_hour) }))
  if (typeof wh.weekday_from_hour === 'number') push('when', 'weekday_from_hour', f2('heard.weeknights_from', { hour: hour(wh.weekday_from_hour) }))
  if (typeof wh.latest_hour === 'number') push('when', 'latest_hour', `${f2('tap.when.latest')} ${hour(wh.latest_hour)}`, undefined, f2('heard.until', { hour: hour(wh.latest_hour) }))

  const wr = i.where ?? {}
  if (typeof wr.place === 'string') push('where', 'place', wr.place)
  // their own word for a neighborhood we don't list rides with the listed area it is near: one chip, their label
  const areas = Array.isArray(wr.areas) ? (wr.areas as string[]) : []
  const typed = typeof wr.base === 'string' && !LA_AREAS.includes(wr.base) && areas.length === 1 ? wr.base : null
  if (typed) push('where', 'areas', f2('where.near_area', { place: typed, area: areas[0] }), areas[0], typed)
  else each('where', 'areas')
  // "Parts of the city" says nothing the area chips don't (R7: a filler chip); every other scope is a real choice.
  // R9: a named place already says "another city" / "a trip abroad" ("San Diego" + "Another city" read as two places)
  const placeNamed = typeof wr.place === 'string' && wr.place.trim() !== ''
  const placeScope = wr.scope === 'other_city' || wr.scope === 'trip'
  if (typeof wr.scope === 'string' && wr.scope !== 'areas' && !(placeNamed && placeScope)) push('where', 'scope', SCOPE[wr.scope] ? f2(SCOPE[wr.scope]) : wr.scope)
  if (!typed && typeof wr.base === 'string' && !areas.includes(wr.base)) push('where', 'base', wr.base)
  each('where', 'transport', (t) => (TRANSPORT[t] ? f2(TRANSPORT[t]) : t))
  if (typeof wr.max_travel_min === 'number') push('where', 'max_travel_min', f2('where.travel_value', { minutes: wr.max_travel_min }))

  each('favorites', 'loved')
  each('favorites', 'been')
  each('interests', 'passions')
  const weights = i.interests?.weights as Record<string, number> | undefined
  // a date already says "date night"; the tag would be the same meaning twice (and reads as an evening)
  const dateSaid = i.occasion?.kind === 'date'
  if (weights) for (const [k, v] of Object.entries(weights)) if (v > 0 && !(dateSaid && k === 'date-night')) push('interests', 'weights', tagLabel(k), k)

  const p = i.physical ?? {}
  if (typeof p.level === 'number' && LEVEL[p.level]) push('physical', 'level', f2(LEVEL[p.level]))
  if (typeof p.max_walk_min === 'number') push('physical', 'max_walk_min', p.max_walk_min <= 15 ? f2('tap.active.walk_short') : p.max_walk_min <= 30 ? f2('tap.active.walk_medium') : f2('tap.active.walk_long'))
  if (typeof p.thrill === 'number') push('physical', 'thrill', Math.abs(p.thrill) < 0.15 ? f2('intake.no_preference') : `${f2('tap.active.thrills')}: ${f2(p.thrill > 0 ? 'tap.preference.yes' : 'tap.preference.no')}`)
  push('physical', 'water_ok', yesNo(f2('tap.active.water'), p.water_ok))
  push('physical', 'heights_ok', yesNo(f2('tap.active.heights'), p.heights_ok))

  const s = i.social ?? {}
  push('social', 'energy', lean(s.energy, 'tap.social.calm', 'tap.social.lively'))
  push('social', 'crowds', lean(s.crowds, 'tap.social.small', 'tap.social.crowds_ok'))
  push('social', 'strangers', lean(s.strangers, 'tap.social.own_company', 'tap.social.meet'))

  const o = i.occasion ?? {}
  if (typeof o.kind === 'string') push('occasion', 'kind', OCCASIONS.has(o.kind) ? f2(`heard.occasion.${o.kind}`) : slugLabel(o.kind, true))
  if (typeof o.note === 'string') push('occasion', 'note', o.note)

  const b = (server ? server.budget : i.budget) ?? {}
  if (typeof b.tier === 'string') push('budget', 'tier', TIER[b.tier] ? f2(TIER[b.tier]) : b.tier)
  if (typeof b.per_person_usd === 'number') push('budget', 'per_person_usd', f2('common.per_person', { price: `$${b.per_person_usd}` }))
  if (b.splurge_ok === true) push('budget', 'splurge_ok', f2('tap.budget.one_splurge'))

  if (typeof i.pace?.kind === 'string' && ['one', 'relaxed', 'packed'].includes(i.pace.kind)) push('pace', 'kind', f2(`tap.pace.${i.pace.kind}`))

  const n = i.novelty ?? {}
  push('novelty', 'experimental', lean(n.experimental, 'tap.novelty.classics', 'tap.novelty.experimental'))
  if (typeof n.iykyk === 'number') push('novelty', 'iykyk', f2(n.iykyk >= 0.5 ? 'tap.novelty.hidden' : 'tap.novelty.famous'))
  if (typeof n.repeats_ok === 'boolean') push('novelty', 'repeats_ok', f2(n.repeats_ok ? 'tap.novelty.repeats' : 'tap.novelty.new'))

  const m = i.mode ?? {}
  push('mode', 'hands_on', lean(m.hands_on, 'tap.mode.watch', 'tap.mode.do'))
  push('mode', 'learn', lean(m.learn, 'tap.mode.enjoy', 'tap.mode.learn'))

  const st = i.setting ?? {}
  push('setting', 'outdoors', lean(st.outdoors, 'tap.setting.indoors', 'tap.setting.outdoors'))
  push('setting', 'nature', lean(st.nature, 'tap.setting.city', 'tap.setting.nature'))

  // a lean that contradicts the hours they gave ("Early starts" beside "Start no earlier than 11 AM") is left out
  const late = i.rhythm?.lateness
  const clash = typeof late === 'number' && ((late < 0 && typeof wh.earliest_hour === 'number' && wh.earliest_hour >= 10) || (late > 0 && typeof wh.latest_hour === 'number' && wh.latest_hour <= 20))
  if (!clash) push('rhythm', 'lateness', lean(late, 'tap.rhythm.early', 'tap.rhythm.late'))

  const sr = i.structure ?? {}
  push('structure', 'planner', lean(sr.planner, 'tap.structure.spontaneous', 'tap.structure.book'))
  if (typeof sr.book_ahead_ok === 'boolean' && typeof sr.planner !== 'number') push('structure', 'book_ahead_ok', f2(sr.book_ahead_ok ? 'tap.structure.book' : 'tap.structure.spontaneous'))
  if (typeof sr.lines_ok === 'boolean') push('structure', 'lines_ok', f2(sr.lines_ok ? 'tap.structure.lines' : 'tap.structure.no_lines'))

  const fd = i.food ?? {}
  if (typeof fd.role === 'string' && ['skip', 'side', 'main'].includes(fd.role)) push('food', 'role', f2(`tap.food.${fd.role}`), undefined, f2(`heard.food.${fd.role}`))
  const alc = typeof fd.alcohol === 'string' ? ({ no: 'tap.food.alcohol_none', some: 'tap.food.alcohol_optional', yes: 'tap.food.alcohol_yes' } as Record<string, string>)[fd.alcohol] : null
  if (alc) push('food', 'alcohol', f2(alc))
  each('food', 'diet')
  if (typeof fd.adventurous === 'number') push('food', 'adventurous', Math.abs(fd.adventurous) < 0.15 ? f2('intake.no_preference') : fd.adventurous > 0 ? f2('tap.food.adventurous') : `${f2('tap.food.adventurous')}: ${f2('tap.preference.no')}`)

  const pr = i.practical ?? {}
  for (const k of ['kids', 'dog', 'step_free', 'seating']) if (pr[k] === true) push('practical', k, f2(`tap.practical.${k}`))

  each('never', 'tags', tagLabel)
  each('never', 'words')

  const ab = i.about ?? {}
  if (typeof ab.age_band === 'string') push('about', 'age_band', ab.age_band)
  if (typeof ab.gender === 'string') push('about', 'gender', ab.gender)
  if (typeof ab.local === 'string' && ['local', 'new', 'visiting'].includes(ab.local)) push('about', 'local', f2(`tap.about.${ab.local}`), undefined, f2(`heard.about.${ab.local}`))
  each('about', 'languages')

  // "No preference" shows only while the topic has nothing else; answering it normally supersedes it.
  for (const g of noPrefOf(i)) if (GROUPS.includes(g as Group) && !out.some((c) => c.group === g)) push(g as Group, 'no_pref', f2('intake.no_preference'), g)
  return out
}


/**
 * The playback line for what was just heard, from the intake as it is now (R8: a chip they removed stayed in "Got
 * it"). A heard chip counts while the current chips still hold it (same id, or the same words under another topic).
 */
export function playbackNow(heard: Chip[], current: Chip[]): string | null {
  if (!heard.length) return null
  const ids = new Set(current.map((c) => c.id))
  const byLabel = new Map(current.map((c) => [norm(c.label), c]))
  const byId = new Map(current.map((c) => [c.id, c]))
  // the current wording wins ("A group · 5" once the size is known)
  const still = heard.map((c) => (ids.has(c.id) ? byId.get(c.id)! : byLabel.get(norm(c.label)) ?? null)).filter((c): c is Chip => c !== null)
  return playback(still)
}

/** What was just heard, played back in their terms: up to three chips, "Atwater Village, Sat, Oct 3, from 11 AM". */
export function playback(heard: Chip[]): string | null {
  const parts = heard.filter((c) => c.field !== 'no_pref').slice(0, 3).map((c) => c.short ?? c.label)
  return parts.length ? parts.join(', ') : null
}

/** A removal as `set` patches. Union lists and tag weights merge on the server, so they are cleared then rewritten. */
export function removalPatches(i: Intake, c: Chip): IntakeBody['set'][] {
  if (c.field === 'no_pref') {
    const rest = noPrefOf(i).filter((g) => g !== c.group)
    return rest.length ? [{ meta: { no_pref: null } }, { meta: { no_pref: rest } }] : [{ meta: { no_pref: null } }]
  }
  const cur = i[c.group]?.[c.field]
  if (c.field === 'weights' && cur && typeof cur === 'object') {
    const rest = Object.fromEntries(Object.entries(cur as Record<string, number>).filter(([k]) => k !== c.value))
    return Object.keys(rest).length ? [{ [c.group]: { weights: null } }, { [c.group]: { weights: rest } }] : [{ [c.group]: { weights: null } }]
  }
  if (Array.isArray(cur) && c.value !== undefined) {
    const rest = cur.filter((x) => String(x) !== String(c.value))
    if (!rest.length) return [{ [c.group]: { [c.field]: null } }]
    return isUnion(c.group, c.field) ? [{ [c.group]: { [c.field]: null } }, { [c.group]: { [c.field]: rest } }] : [{ [c.group]: { [c.field]: rest } }]
  }
  if (c.group === 'who' && c.field === 'kind') return [{ who: { kind: null, size: null } }]
  if (c.group === 'when' && c.field === 'from') return [{ when: { from: null, to: null } }]
  if (c.group === 'practical' && !['kids', 'dog', 'step_free', 'seating'].some((k) => i.practical?.[k] === true)) return [{ practical: null }]
  return [{ [c.group]: { [c.field]: null } }]
}

